import { ImportService } from '@amzn/awscat-portfolio-assessment-service-client';
import { FileRecord, PreviewResult, PropertyConfig } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import React, { PureComponent } from 'react';

import { constants } from '../../../common/Localization';
import Table from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { format } from '../../../common/util';
import { RecordColCell } from './common-components/ColumnDefinitions';
import { MissingValueWarning } from './common-components/MissingValueWarning';
import { RESOURCE_TYPES_MAP, WIZARD_STEP_VALUES } from './common-util/resources';
import { convertNumberToString, displayDecimalAsPercentage } from './common-util/util';
import { withWizardContext } from './contexts/WizardContext';
import { WizardContentProps } from './WizardContentProps';

const stepName: WIZARD_STEP_VALUES = WIZARD_STEP_VALUES.PREVIEW;

type Props = WizardContentProps & FlashContextInterface;

const importService = new ImportService();
class PreviewStep extends PureComponent<Props> {
    columnDefinitions: ColumnDefinition<FileRecord>[] = [];
    items: FileRecord[] = [];
    missingValueIds: number[] = [];
    async componentDidMount() {
        this.props.setNextAndPrevHandlers(stepName, this.next, this.prev);
        const { portfolioId, importId, type } = this.props.wizardContext;
        try {
            const previewResponse: PreviewResult = await importService.getPreviewData(portfolioId, importId, type);
            const previewData: FileRecord[] = previewResponse.previewData;
            const invalidIds: number[] = previewResponse.missingRowIds ?? [];
            if (!previewData.length) {
                return;
            }
            // Get all properties to display from the first item
            const columnDefinitions: ColumnDefinition<FileRecord>[] = this.props.propertiesConfig.filter((propertyConfig: PropertyConfig) => {
                return typeof previewData[0][propertyConfig.value] !== 'undefined';
            }).map((propertyConfig: PropertyConfig) => {
                return {
                    cell: item => RecordColCell(item, propertyConfig.value, propertyConfig.webUOM),
                    header: propertyConfig.label,
                    id: propertyConfig.value,
                    type: ColumnTypes.TEXT,
                };
            });
            this.columnDefinitions = columnDefinitions;
            this.items = previewData;
            this.missingValueIds = invalidIds.map((id: number) => {
                return id + 1;
            });
            displayDecimalAsPercentage(this.items, this.props.propertiesConfig);
        } catch (err) {
            this.props.flash.alert(FlashType.error, (err as Error).message, constants.ERR_HEADERS.CANNOT_LOAD_PREVIEW);
        } finally {
            this.props.setLoading(false);
        }
    }

    render() {
        const { items, columnDefinitions, missingValueIds } = this;
        const { type } = this.props.wizardContext;
        return (
            <div>
                <MissingValueWarning
                    missingIds={missingValueIds}
                />
                <div id='preview-table'>
                    <Table
                        resourceName={`${RESOURCE_TYPES_MAP[type].label}`}
                        columnDefinitions={columnDefinitions as ColumnDefinition<unknown>[]}
                        items={items}
                        loading={this.props.loading}
                    />
                </div>
            </div>
        );
    }

    next = async (): Promise<void> => {
        const { portfolioId, importId, type } = this.props.wizardContext;
        await importService.ingestPreviewData(portfolioId, importId, type);
        this.props.flash.alert(
            FlashType.success,
            format(constants.SUCCESS_IMPORT_MESSAGE, [convertNumberToString(this.items.length), RESOURCE_TYPES_MAP[type].label]),
            '');
    };

    prev = async (): Promise<void> => {};
}

export default withFlashContext(withWizardContext(PreviewStep));
